import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  InputBase,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import { debounce } from "lodash";

import { FeedEntry, Medium } from "protogen/feed_pb";
import {
  useDeleteEmailDraft,
  useSendEmailDraft,
  useUpdateEmailDraft,
} from "services/email";
import { EmailMessage } from "protogen/conversation_pb";
import DocumentEditor, { Handle } from "../editor/DocumentEditor";
import useIsMobile from "../hooks/useIsMobile";
import { CurrentUserContext } from "../context/RequireAuth";
import {
  initialDraftFromDraft,
  initialLocalDraft,
  isDraftSavable,
  isDraftSendable,
} from "./utils";
import AddressFields from "./AddressFields";
import { LocalDraft } from "./types";
import { Trash2, Clock, ChevronDown } from "lucide-react";
import useGetEmailAutocompleteEntries from "./useGetEmailAutocompleteEntries";
import { stripImgSrc } from "../../common/utils";
import ScheduleSend from "../activity/ScheduledMessageDialog";
import ScheduledMessageByline from "../activity/ScheduledMessageByline";
import { CreateOrUpdateEmailDraftRequest } from "protogen/email_service_pb";
import FlexBox from "components/common/FlexBox";
import { fetchGetDocument } from "services/documents";
import TemplatesButton from "../common/TemplatesButton";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "../common/ConfirmationDialog";

export interface EmailComposeProps {
  onSent: (e: FeedEntry) => void;
  onClose: () => void;
  onRemove: (ref: string, m: Medium) => void;
  inReplyTo?: EmailMessage | null;
  initialDraft?: EmailMessage | null;
  isForward?: boolean;
  initialTo?: string[];
  initialCC?: string[];
  setSubjectTitle?: (t: string) => void;
  setSendDisabled?: (d: boolean) => void;
  setHovering?: (h: boolean) => void;
  minHeight?: string;
  maxHeight?: string;
  inlineView?: boolean;
  refreshableChange?: () => void;
  initialContent?: string;
  initialSubject?: string;
}

const randstr = (prefix: string) => {
  return Math.random()
    .toString(36)
    .replace("0.", prefix || "");
};

export type ComposeHandle = {
  reset: () => void;
  handleSend: () => void;
};

export default forwardRef<ComposeHandle, EmailComposeProps>(
  (
    {
      onSent,
      onClose,
      onRemove,
      inReplyTo = null,
      initialDraft = null,
      isForward = false,
      initialTo,
      initialCC,
      setSubjectTitle,
      setSendDisabled,
      setHovering,
      minHeight = "55vh",
      maxHeight = "55vh",
      inlineView = false,
      refreshableChange,
      initialContent,
      initialSubject,
    }: EmailComposeProps,
    ref,
  ) => {
    const isMobile = useIsMobile();
    const hideActions = isMobile && !inlineView;
    const isSavingFirstDraft = useRef(false);
    const currentUser = useContext(CurrentUserContext);
    const [autosaving, setAutosaving] = useState<Promise<any> | null>(null);
    const [key, setKey] = useState(randstr("body-"));
    const documentRef = useRef<Handle | null>(null);
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [sendMenuAnchor, setSendMenuAnchor] = useState<null | HTMLElement>(
      null,
    );
    const [uploadState, setUploadState] = useState({
      uploadsInProgress: false,
      uploadPercentage: 0,
    });
    const [draft, setDraft] = useState<LocalDraft>(
      initialLocalDraft(
        initialDraft,
        inReplyTo,
        currentUser,
        isForward,
        initialTo,
        initialCC,
        initialContent,
        initialSubject,
      ),
    );
    const draftRef = useRef<LocalDraft>(draft);
    useEffect(() => {
      draftRef.current = draft; // <-- cache current value
    }, [draft]);
    const { autocompleteEntries } = useGetEmailAutocompleteEntries({
      // make a prop
      familyRef: undefined,
    });
    const reset = () => {
      const localDraft = initialLocalDraft(
        initialDraft,
        inReplyTo,
        currentUser,
        isForward,
        initialTo,
        initialCC,
        initialContent,
        initialSubject,
      );
      // withAttachments(localDraft.attachments || []);
      setDraft(localDraft);
      setKey(randstr("body-"));
      // Reset template tracking when creating new draft
      setLoadedTemplateId(null);
      setOriginalTemplateContent(null);
      setIsUserContent(false);
    };
    useEffect(() => {
      // TODO: Get email draft from server and then update local draft
      reset();
    }, [
      initialDraft,
      inReplyTo,
      currentUser,
      isForward,
      initialContent,
      initialSubject,
    ]);
    useEffect(() => {
      setDraft((draft) => ({
        ...draft,
        ...(!draft.ref && {
          to: initialTo || draft.to,
          cc: initialCC || draft.cc,
        }),
      }));
    }, [initialTo, initialCC]);

    const { loading: deleteLoading, request: deleteRequest } =
      useDeleteEmailDraft(() => {
        // withAttachments([]);
        onRemove(draft.ref!, Medium.EMAIL);
        reset();
        onClose();
      });
    const [sendLoading, setSendLoading] = useState(false);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
    const [templateKey, setTemplateKey] = useState(0);
    const [loadedTemplateId, setLoadedTemplateId] = useState<string | null>(
      null,
    );
    const [originalTemplateContent, setOriginalTemplateContent] = useState<{
      htmlContent: string;
      textContent: string;
    } | null>(null);
    const [isUserContent, setIsUserContent] = useState(false);
    const confirmationDialog = useConfirmationDialog();
    const { request: sendRequest } = useSendEmailDraft((r) => {
      // withAttachments([]);
      setDraft(
        initialLocalDraft(
          null,
          null,
          currentUser,
          false,
          initialTo,
          initialCC,
          initialContent,
        ),
      );
      onSent(
        new FeedEntry({
          ref: r.email.ref,
          senderRef: r.email.senderRef,
          timestampSec: r.email.timestampSec,
          medium: Medium.EMAIL,
          emailMessage: r.email,
          recipientRefs: [],
        }),
      );
      reset();
      onClose();
    });
    const { loading: updateLoading, request: updateDraftRequest } =
      useUpdateEmailDraft((r) => {
        if (documentRef.current) {
          documentRef.current?.updateAttachments(r.email?.attachments || []);
        }
        setDraft((d) => ({
          ...initialDraftFromDraft(r.email),
          // Let's not override these local changes.
          to: d.to,
          cc: d.cc,
          bcc: d.bcc,
          subject: d.subject,
          textContent: d.textContent,
          htmlContent: d.htmlContent,
          scheduledDateSecUTC: d.scheduledDateSecUTC,
        }));
      });

    const loading = deleteLoading || sendLoading || updateLoading;
    const handleSave = async (currentDraft: LocalDraft, isAuto: boolean) => {
      // Note! It's important that this function not rely on any state which is likely stale
      // since it is debounced.
      if (currentDraft.ref) {
        isSavingFirstDraft.current = false;
      } else if (!isSavingFirstDraft.current) {
        isSavingFirstDraft.current = true;
      } else {
        return;
      }

      // If there are uploads in progress and this is an autosave, don't save yet
      // The editor triggers a "onSave" when the uploads are done.
      // Not required to trigger or track another save.
      if (isAuto && uploadState.uploadsInProgress) {
        return;
      }

      const resultPromise = updateDraftRequest(
        new CreateOrUpdateEmailDraftRequest({
          draftRef: currentDraft?.ref || "",
          draft: {
            replyToRef: inReplyTo?.ref || "",
            subject: currentDraft?.subject || "",
            textContent: currentDraft?.textContent || "",
            htmlContent: stripImgSrc(currentDraft?.htmlContent) || "",
            attachments: currentDraft.uploadAttachments,
            toAddresses: currentDraft?.to || [],
            ccAddresses: currentDraft?.cc || [],
            bccAddresses: currentDraft?.bcc || [],
            isForwarded: currentDraft?.isForwarded || false,
            includePreviousAttachments:
              currentDraft?.includePreviousAttachments || false,
            scheduledDateSecUTC: currentDraft?.scheduledDateSecUTC
              ? BigInt(currentDraft?.scheduledDateSecUTC)
              : undefined,
            emailType: currentDraft?.emailType || "",
          },
        }),
      );
      if (isAuto) setAutosaving(resultPromise);
      const result = await resultPromise;
      if (isAuto) setAutosaving(null);
      return result;
    };

    const handleSendEmail = async () => {
      if (deleteLoading || sendLoading) return;
      if (!isDraftSendable(draft)) return;
      setSendLoading(true);
      if (autosaving) {
        await autosaving;
      }
      const finalDraft = await handleSave(draft, false);
      if (finalDraft) {
        await sendRequest({ draftRef: finalDraft.email.ref });
      }
      setSendLoading(false);
    };

    const handleScheduleSend = async (date: Date) => {
      if (deleteLoading || sendLoading) return;
      if (!isDraftSendable(draft)) return;
      setSendLoading(true);
      if (autosaving) {
        await autosaving;
      }
      const resp = await handleSave(
        {
          ...draft,
          scheduledDateSecUTC: Math.ceil((date?.getTime() || 0) / 1000),
        },
        false,
      );
      // Tell the inbox to update the
      refreshableChange?.();
      setSendLoading(false);
      setScheduleDialogOpen(false);
      if (resp) {
        onClose();
      }
    };

    // Use the debounce function to save data with a delay
    const debouncedSave = useMemo(
      () =>
        debounce(handleSave, 500, {
          trailing: true,
        }), // Adjust the delay as needed (e.g., 1000ms = 1 second)
      [inReplyTo, initialTo, initialCC, uploadState.uploadsInProgress],
    );

    const updateDraft = (d: LocalDraft) => {
      setDraft(d);

      if (isDraftSavable(d)) {
        debouncedSave(d, true);
      }
    };

    const handleContentChange = (d: LocalDraft) => {
      updateDraft(d);

      // If user is manually editing content, mark as user content
      if (loadedTemplateId && originalTemplateContent) {
        const currentHtml = d.htmlContent || "";
        const currentText = d.textContent || "";
        const originalHtml = originalTemplateContent.htmlContent;
        const originalText = originalTemplateContent.textContent;

        // If content differs from original template, mark as user content
        if (currentHtml !== originalHtml || currentText !== originalText) {
          setIsUserContent(true);
        }
      } else {
        // If no template is loaded, this is user content
        setIsUserContent(true);
      }
    };

    // It's annoying to visually change the enabled state here.
    // const isSendDisabled = loading || autosaving || !isDraftSendable(draft);
    const isSendDisabled =
      deleteLoading ||
      sendLoading ||
      !isDraftSendable(draft) ||
      uploadState.uploadsInProgress;

    useEffect(() => {
      setSendDisabled?.(isSendDisabled);
    }, [isSendDisabled]);

    const deleteAction = (
      <Trash2
        size={20}
        onClick={() => {
          if (!loading) {
            if (draft.ref) {
              deleteRequest({ draftRef: draft.ref });
            } else {
              onClose();
            }
          }
        }}
        style={{
          color: sendLoading || deleteLoading ? "#00000042" : "#8E9598",
          cursor: !(sendLoading || deleteLoading) ? "pointer" : undefined,
        }}
      />
    );

    const hasExistingContent = () => {
      const hasHtmlContent =
        draft.htmlContent && draft.htmlContent.trim().length > 0;
      const hasTextContent =
        draft.textContent && draft.textContent.trim().length > 0;
      return hasHtmlContent || hasTextContent;
    };

    const handleTemplateSelect = (documentId: string) => {
      if (hasExistingContent()) {
        if (isUserContent) {
          // If the user has made any changes to the message, show the confirmation dialog.
          confirmationDialog.openDialog(
            () => {
              fetchTemplateDocument(documentId);
            },
            () => {
              // Cancel callback - no action needed
            },
          );
        } else {
          fetchTemplateDocument(documentId);
        }
      } else {
        fetchTemplateDocument(documentId);
      }
    };

    const fetchTemplateDocument = async (documentId: string) => {
      setIsLoadingTemplate(true);

      try {
        const response = await fetchGetDocument(documentId);
        if (!response || !response.document) {
          console.error("Template document not found");
          return;
        }

        // Update the email content with the template document content
        let htmlContent = response.document.note?.content?.payload || "";
        let textContent = response.document.note?.content?.textContent || "";

        // Store the original template content before modifications
        const originalHtml = htmlContent;
        const originalText = textContent;

        // Add extra space to the bottom of the email
        if (htmlContent) {
          htmlContent = htmlContent + "<br><br><br>";
        }
        if (textContent) {
          textContent = textContent + "\n\n\n";
        }

        if (htmlContent || textContent) {
          updateDraft({
            ...draft,
            htmlContent: htmlContent,
            textContent: textContent,
          });

          // Track which template was loaded and store original content
          setLoadedTemplateId(documentId);
          setOriginalTemplateContent({
            htmlContent: originalHtml,
            textContent: originalText,
          });
          // Reset user content flag since we're loading a template
          setIsUserContent(false);

          // Force DocumentEditor to re-render with new content
          setTemplateKey((prev) => prev + 1);
        }
      } catch (err) {
        console.error("Error fetching template document:", err);
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    useImperativeHandle(ref, () => ({
      reset: () => reset(),
      handleSend: () => handleSendEmail(),
    }));
    return (
      <Box display="flex" flexDirection="column" height="100%">
        <>
          <Box>
            <AddressFields
              autofocusTo={draft.to.length === 0}
              recipients={{
                to: draft.to,
                cc: draft.cc,
                bcc: draft.bcc,
              }}
              setRecipients={(r) =>
                updateDraft({ ...draft, to: r.to, cc: r.cc, bcc: r.bcc })
              }
              autocompleteEntries={autocompleteEntries}
            />
          </Box>
          <FlexBox flex={0}>
            <InputBase
              key={`subject-${key}`}
              disabled={sendLoading || deleteLoading}
              autoComplete="off"
              value={draft.subject}
              onChange={(e) => {
                updateDraft({ ...draft, subject: e.target.value || "" });
              }}
              placeholder="Subject"
              sx={{ fontFamily: "sans-serif" }}
              fullWidth
              autoFocus={draft.to.length > 0 && !draft.subject}
              onBlur={() => {
                setSubjectTitle?.(draft.ref ? draft.subject : "");
              }}
            />

            {!isMobile && (
              <TemplatesButton
                isLoadingTemplate={isLoadingTemplate}
                onTemplateSelect={handleTemplateSelect}
                getTemplateContent={() => ({
                  textContent: draft.textContent,
                  htmlContent: draft.htmlContent,
                  title: draft.subject,
                })}
              />
            )}
          </FlexBox>

          <Divider sx={{ marginTop: "8px" }} />

          <DocumentEditor
            autofocus={draft.to.length > 0 && !!draft.subject}
            ref={documentRef}
            key={`${key}-${templateKey}`}
            editorMinHeight={minHeight}
            editorMaxHeight={maxHeight}
            placeholder="Type your message..."
            disabled={sendLoading || deleteLoading}
            onUploadStateChange={setUploadState}
            setContent={(c) => {
              if (
                c.html === draft.htmlContent &&
                c.text === draft.textContent &&
                // Only "new" uploads have a validation key set, any if there are any new uploads
                // we should trigger a save.
                c.attachments.every((a) => !a.validationKey)
              )
                return;
              handleContentChange({
                ...draft,
                textContent: c.text,
                htmlContent: c.html,
                uploadAttachments: c.attachments,
              });
            }}
            initialContent={draft.htmlContent || draft.textContent || undefined}
            initialAttachments={draft.attachments || undefined}
            leftPrimaryAction={
              <>
                <FlexBox
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: isMobile ? "100%" : "auto" }}
                  flex={0}
                >
                  {isMobile && (
                    <TemplatesButton
                      isLoadingTemplate={isLoadingTemplate}
                      onTemplateSelect={handleTemplateSelect}
                    />
                  )}
                  {!hideActions && (
                    <Box display="flex" alignItems="center">
                      <Button
                        size="small"
                        onClick={handleSendEmail}
                        disabled={isSendDisabled}
                        variant="contained"
                        sx={{
                          alignSelf: isMobile ? "self-end" : undefined,
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          padding: "6px 12px 6px 20px",
                        }}
                      >
                        Send
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => setSendMenuAnchor(e.currentTarget)}
                        disabled={isSendDisabled}
                        variant="contained"
                        sx={{
                          alignSelf: isMobile ? "self-end" : undefined,
                          minWidth: "32px",
                          padding: "6px",
                          paddingRight: "12px",
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          borderLeft: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        <ChevronDown size={20} />
                      </Button>
                    </Box>
                  )}
                </FlexBox>
                {!hideActions && (
                  <Menu
                    anchorEl={sendMenuAnchor}
                    open={Boolean(sendMenuAnchor)}
                    onClose={() => setSendMenuAnchor(null)}
                  >
                    <MenuItem
                      disabled={!!draft.scheduledDateSecUTC}
                      onClick={() => {
                        setSendMenuAnchor(null);
                        setScheduleDialogOpen(true);
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Clock size={16} />
                        Schedule send
                      </Box>
                    </MenuItem>
                  </Menu>
                )}
              </>
            }
            secondaryAction={
              !isMobile ? <FlexBox>{deleteAction}</FlexBox> : undefined
            }
            editActions={isMobile ? [deleteAction] : undefined}
            attachmentsEnabled={true}
            setDragState={setHovering}
            // Override the font family so we see what the user will see.
            sx={{
              fontFamily: "sans-serif",
              height: "100%",
              ".editor-actions": {
                marginBottom: "20px",
              },
            }}
            scrollable={true}
            attachmentListSuffix={
              <>
                {draft.scheduledDateSecUTC ? (
                  <ScheduledMessageByline
                    scheduledTime={BigInt(draft.scheduledDateSecUTC)}
                    scheduledManager={{
                      loading: sendLoading,
                      handleSendNow: handleSendEmail,
                      handleUpdateSchedule: handleScheduleSend,
                      handleDelete: async () => {
                        updateDraft({
                          ...draft,
                          scheduledDateSecUTC: undefined,
                        });
                        // Tell the inbox to update the state, give time for an autosave to happen.
                        setTimeout(() => {
                          refreshableChange?.();
                        }, 1000);
                      },
                    }}
                    boxed={true}
                  />
                ) : null}
                {draft.isReply || draft.isForwarded ? (
                  <IncludePreviousAttachmentsCheckbox
                    onClick={() => {
                      updateDraft({
                        ...draft,
                        includePreviousAttachments:
                          !draft.includePreviousAttachments,
                      });
                    }}
                    disabled={sendLoading || deleteLoading}
                    checked={draft.includePreviousAttachments}
                  />
                ) : undefined}
              </>
            }
          />
          <ScheduleSend
            open={scheduleDialogOpen}
            onClose={() => setScheduleDialogOpen(false)}
            onUpdate={handleScheduleSend}
          />
        </>
        <ConfirmationDialog
          {...confirmationDialog.dialogProps}
          yesButtonText="Yes, continue"
          noButtonText="Cancel"
          title="Replace your current message?"
          content="Loading an email template will replace your current message and any changes will be lost."
        />
      </Box>
    );
  },
);

const IncludePreviousAttachmentsCheckbox = ({
  onClick,
  disabled,
  checked,
}: {
  onClick: () => void;
  disabled: boolean;
  checked: boolean;
}) => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      onClick={onClick}
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.02)",
        padding: "14px 35px 14px 20px",
        borderRadius: "8px",
        gap: "8px",
        position: "relative",
        cursor: "pointer",
        alignItems: "center",
        marginBottom: "8px",
      }}
    >
      <Checkbox sx={{ padding: 0 }} disabled={disabled} checked={checked} />
      <Typography variant="body">Include previous attachments</Typography>
    </Box>
  );
};
